import React from 'react';
import { Modal, Button, IconSettings } from '@salesforce/design-system-react';
import { CQAppConstant } from 'app-constants';

interface CQConfirmationModalProps {
    /** Controls whether the modal is visible */
    isOpen: boolean;

    /** Modal title heading */
    heading: string;

    /** Main message content displayed in modal body */
    message: string | React.ReactNode;

    /** Label for the confirm button (if not provided, confirm button is not rendered) */
    confirmLabel?: string;

    /** Label for the cancel button (defaults to 'Cancel') */
    cancelLabel?: string;

    /** Visual style of modal prompt - converts to the Modal's prompt property for different info types */
    promptStyle?: typeof CQAppConstant.PROMPT_STYLE[keyof typeof CQAppConstant.PROMPT_STYLE];

    /** Button style for the confirm action (defaults to 'brand') */
    confirmActionStyle?: typeof CQAppConstant.CONFIRM_ACTION_STYLE[keyof typeof CQAppConstant.CONFIRM_ACTION_STYLE];

    /** Whether an action is currently in progress (disables buttons, hides close) */
    isActionInProgress?: boolean;

    /** Whether the confirm button should be disabled */
    disableConfirmButton?: boolean;

    /** Label shown on confirm button while action is in progress */
    progressLabel?: string;

    /** Callback fired when user clicks confirm button (optional if confirm button is not shown) */
    onConfirm?: () => void;

    /** Callback fired when user clicks cancel or close */
    onCancel: () => void;

    /** Optional additional content displayed below the main message */
    supportingDetails?: React.ReactNode;

    /** Whether clicking outside modal closes it (defaults to true, ignored during action) */
    canDismissOutsideClick?: boolean;

    /** Size of the modal (defaults to 'small'). Use `MODAL_SIZE.COMPACT` for a truly small modal. */
    size?: typeof CQAppConstant.MODAL_SIZE[keyof typeof CQAppConstant.MODAL_SIZE];
}

function CQConfirmationModal({
    isOpen,
    heading,
    message,
    confirmLabel,
    cancelLabel = CQAppConstant.BUTTON_LABELS.CANCEL,
    confirmActionStyle = CQAppConstant.CONFIRM_ACTION_STYLE.BRAND,
    promptStyle,
    isActionInProgress = false,
    disableConfirmButton = false,
    progressLabel,
    onConfirm,
    onCancel,
    supportingDetails,
    canDismissOutsideClick = true,
    size = CQAppConstant.MODAL_SIZE.SMALL
}: CQConfirmationModalProps) {
    return (
        <IconSettings iconPath="/assets/icons">
            <Modal
                isOpen={isOpen}
                heading={heading}
                // COMPACT omits `size` for SLDS's smallest (unstyled) modal width
                {...(size !== CQAppConstant.MODAL_SIZE.COMPACT && { size })}
                {...(promptStyle && { prompt: promptStyle })}
                hasCloseButton={!isActionInProgress}
                dismissOnClickOutside={canDismissOutsideClick && !isActionInProgress}
                onRequestClose={!isActionInProgress ? onCancel : undefined}
                footer={[
                    <Button
                        key={CQAppConstant.BUTTON_LABELS.CANCEL}
                        label={cancelLabel}
                        variant="neutral"
                        disabled={isActionInProgress}
                        onClick={onCancel}
                    />,
                    confirmLabel && (
                        <Button
                            key={CQAppConstant.BUTTON_LABELS.CONFIRM}
                            label={isActionInProgress ? (progressLabel || `${confirmLabel}...`) : confirmLabel}
                            variant={confirmActionStyle}
                            disabled={isActionInProgress || disableConfirmButton}
                            onClick={onConfirm}
                            className="slds-m-left_x-small"
                        />
                    )
                ]}
            >
                <div className="slds-text-body_regular slds-p-around_medium ">
                    <div>
                        {message}
                    </div>
                    {supportingDetails}
                </div>
            </Modal>
        </IconSettings>
    );
}

export default CQConfirmationModal;
